import React, { useContext, useMemo } from 'react';
import type { RollingChartProps, RollingPeriodEnum } from './types';
import { getAnalysisLabels } from 'venn-utils';
import AnalysisLineChart from './internal/AnalysisLineChart';
import { AnalysisViewContext } from '../../contexts';
import { PERIODS } from './internal/AnalysisChartLegend';
import useCustomRollingWindow from './internal/useCustomRollingWindow';
import { isNil } from 'lodash';

const AnalysisRollingSharpeChart = ({
  frequency,
  analysisGroup,
  subject,
  actualTimeFrame,
  relative,
  noChartErrorComponent,
  noChartTrackingProps,
  downloadMetaData,
  categoryActive,
}: RollingChartProps) => {
  const fullPeriodLabels = useMemo(() => {
    const titleType = getAnalysisLabels(subject.type).main;
    return {
      absolute: `${titleType}'s Full-History Sharpe Ratio`,
      relative: `${titleType}'s Full-History Information Ratio`,
    };
  }, [subject.type]);

  const { rollingSharpePeriod, onUpdateAnalysisViewParam } = useContext(AnalysisViewContext);
  const { chartDataWithRollingPeriods, customPeriodLoading, setOptimisticLoading } = useCustomRollingWindow(
    subject,
    analysisGroup,
    rollingSharpePeriod,
    relative,
    categoryActive,
    actualTimeFrame,
    'ROLLING_SHARPE',
    'rollingSharpe',
    fullPeriodLabels,
    noChartTrackingProps?.trackingId,
  );

  const header = relative ? 'Rolling Information Ratio' : 'Rolling Sharpe Ratio';
  return (
    <AnalysisLineChart
      defaultRollingPeriod={PERIODS.find(({ key }) => key === rollingSharpePeriod)}
      rollingPeriodSettings={rollingSharpePeriod}
      loadingSerieData={customPeriodLoading}
      onRollingPeriodChange={(updatedPeriodKey?: RollingPeriodEnum, customPeriodMonths?: number) => {
        if (!isNil(updatedPeriodKey)) {
          onUpdateAnalysisViewParam({ rollingSharpePeriod: { period: updatedPeriodKey } });
          return;
        }
        if (!isNil(customPeriodMonths)) {
          setOptimisticLoading(true);
          onUpdateAnalysisViewParam({ rollingSharpePeriod: { customMonths: customPeriodMonths } });
        }
      }}
      allowCustomWindow
      analysisSubject={subject}
      frequency={frequency}
      relative={relative}
      chartDataWithRollingPeriods={chartDataWithRollingPeriods}
      start={actualTimeFrame.startTime}
      end={actualTimeFrame.endTime}
      header={header}
      noChartErrorComponent={noChartErrorComponent}
      noChartTrackingProps={noChartTrackingProps}
      yAxisUnitFormat="ratio"
      exportPNGEnabled
      exportXLSXEnabled
      dataExportEvent={{
        description: 'ROLLING_SHARPE',
        relativeToBenchmark: relative,
        subjectType: subject.type,
        subjectId: subject.id,
        userUploaded: subject.userUploaded,
      }}
      downloadMetaData={downloadMetaData}
    />
  );
};

export default AnalysisRollingSharpeChart;
